import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ResourceVersionService } from '@app/resource-version/services/resource-version.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { FocusStackService } from '@app/shared/services/focus-stack.service';
import { TranslateService } from '@ngx-translate/core';
import {
  PathwayAuthoring,
  PathwayFacade,
  PathwayViewModel,
  PathwayModalSettingsChecks,
} from '@dg/pathways-rsm';
import { Observable } from 'rxjs';

@Component({
  selector: 'dgx-pathway-page-actions',
  templateUrl: './pathway-page-actions.component.html',
  styleUrls: ['./pathway-page-actions.component.scss'],
})
export class PathwayPageActionsComponent implements OnInit {
  public vm$: Observable<PathwayViewModel>;
  public isSubmittingEnrollment: boolean = false;
  public settingsMenuConfig: MenuViewModel[];

  @ViewChild('enrollment') private enrollment: ElementRef;

  constructor(
    private translate: TranslateService,
    private focusStackService: FocusStackService,
    private router: Router,
    private route: ActivatedRoute,
    private resourceVersionService: ResourceVersionService,
    private facade: PathwayFacade,
    private authoring: PathwayAuthoring
  ) {
    this.vm$ = this.facade.vm$;
  }

  public ngOnInit(): void {
    this.settingsMenuConfig = this.getSettingsMenuConfig();
  }

  public async recommend(event: Event) {
    await this.facade.showShare(event.target as HTMLElement);
  }

  public openSettings(popoverTrigger) {
    this.focusStackService.push(popoverTrigger.nativeElement);
    this.showSettingsModal();
  }

  public async confirmDelete(popoverTrigger: ElementRef<any>) {
    this.focusStackService.push(popoverTrigger.nativeElement);
    await this.facade.showConfirmDelete();
  }

  public toggleEditMode(): void {
    this.resourceVersionService
      .isCurrentVersion({
        id: this.facade.snapshot.pathway.id,
        resourceType: this.facade.snapshot.pathway.resourceType,
        resourceVersion: this.facade.snapshot.version,
        showRefreshModal: true,
      })
      .subscribe((isCurrentVersion) => {
        if (isCurrentVersion) {
          this.routeEditPathway();
        }
      });
  }

  public async toggleEnrollment(): Promise<void> {
    await this.facade.toggleEnrollment();
    this.focusStackService.push(this.enrollment.nativeElement);
  }

  private routeEditPathway() {
    this.router.navigate(['pathway'], {
      relativeTo: this.route,
      queryParams: {
        editMode: this.facade.snapshot.isEditMode ? undefined : 'true',
      },
      queryParamsHandling: 'merge',
    });
  }

  private async showSettingsModal(): Promise<void> {
    const checks: PathwayModalSettingsChecks = {
      isChannel: this.facade.snapshot.isChannel,
      disableLearnerSkillsRegistry:
        this.facade.snapshot.disableLearnerSkillsRegistry,
      showPathwayBadgeTrigger: this.facade.snapshot.showPathwayBadgeTrigger,
      isConsumerUser: this.facade.snapshot.isConsumerUser,
    };
    await this.authoring.showSettings(checks);
    this.focusStackService.pop();
  }

  private getSettingsMenuConfig: () => MenuViewModel[] = () => [
    {
      title: this.translate.instant('Core_Settings'),
      id: 'settings',
      defaultAction: (_, popoverTrigger) => {
        this.openSettings(popoverTrigger);
      },
      preventRefocus: true,
    },
    {
      title: this.translate.instant('Core_Delete'),
      id: 'delete',
      defaultAction: (_, popoverTrigger) => {
        this.confirmDelete(popoverTrigger);
      },
      preventRefocus: true,
    },
  ];
}
